import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import { TabView, TabPanel } from 'primereact/tabview';
import api from '../../services/api';

const TIMEZONES = [
  'America/Tijuana',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Hermosillo',
  'America/Chihuahua',
  'America/Mazatlan',
  'America/Cancun',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
];

const CHANNELS = [
  { label: 'WhatsApp', value: 'whatsapp' },
  { label: 'Facebook', value: 'facebook' },
];

const SEND_MODES = ['store', 'api', 'ui'];
const MATCH_BY_OPTIONS = [
  { key: 'id', label: 'Group ID' },
  { key: 'name', label: 'Group Name' },
];

function emptyPub() {
  return {
    channel: 'whatsapp',
    group_id: '',
    group_name: '',
    match_by: ['id'],
    send_mode: ['store'],
    entry_page_slug: '',
    ports: [],
    port_labels: [],
    schedule: ['08:00'],
    timezone: 'America/Tijuana',
    site_url: '',
    active: true,
  };
}

export default function ScheduledPublicationsManager() {
  const toast = useRef(null);
  const [pubs, setPubs] = useState([]);
  const [entryPages, setEntryPages] = useState([]);
  const [history, setHistory] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchPubs = useCallback(async () => {
    try {
      const res = await api.get('/scheduled_publications');
      setPubs(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchEntryPages = useCallback(async () => {
    try {
      const res = await api.get('/scheduled_publications/entry-pages');
      setEntryPages(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/scheduled_publications/history?limit=100');
      setHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchPubs(), fetchEntryPages(), fetchHistory()]).then(() => setLoading(false));
  }, [fetchPubs, fetchEntryPages, fetchHistory]);

  async function savePub() {
    try {
      if (editing._id) {
        await api.put(`/scheduled_publications/${editing._id}`, editing);
      } else {
        await api.post('/scheduled_publications', editing);
      }
      toast.current?.show({ severity: 'success', summary: 'Guardado', life: 2000 });
      setDialogVisible(false);
      setEditing(null);
      await fetchPubs();
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: String(err.message), life: 4000 });
    }
  }

  async function deletePub(id) {
    try {
      await api.delete(`/scheduled_publications/${id}`);
      toast.current?.show({ severity: 'warn', summary: 'Eliminado', life: 2000 });
      await fetchPubs();
    } catch (err) {
      console.error(err);
    }
  }

  async function toggleActive(pub) {
    try {
      await api.put(`/scheduled_publications/${pub._id}`, { active: !pub.active });
      await fetchPubs();
    } catch (err) {
      console.error(err);
    }
  }

  function openNew() {
    setEditing(emptyPub());
    setDialogVisible(true);
  }

  function openEdit(pub) {
    setEditing({ ...pub });
    setDialogVisible(true);
  }

  const epSlug = editing?.entry_page_slug || '';
  const selectedPage = epSlug
    ? entryPages.find((ep) => ep.slug === epSlug || ep.full_slug === epSlug || (ep.slug || ep.full_slug) === epSlug)
    : null;
  const portGroups = selectedPage?.groups || [];
  const availablePorts = portGroups.flatMap((g) => g.ports) || [];

  function setField(key, val) {
    setEditing((prev) => ({ ...prev, [key]: val }));
  }

  function toggleArrayField(key, item) {
    setEditing((prev) => {
      const arr = prev[key] || [];
      const next = arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
      return { ...prev, [key]: next };
    });
  }

  function togglePort(portNum) {
    setEditing((prev) => {
      const ports = prev.ports || [];
      const labels = prev.port_labels || [];
      if (ports.includes(portNum)) {
        return { ...prev, ports: ports.filter((p) => p !== portNum), port_labels: labels.filter((l) => l.number !== portNum) };
      }
      const portInfo = availablePorts.find((p) => p.number === portNum);
      return {
        ...prev,
        ports: [...ports, portNum],
        port_labels: [...labels, { number: portNum, label: portInfo?.label || '' }],
      };
    });
  }

  function addScheduleSlot() {
    setField('schedule', [...(editing?.schedule || []), '12:00:00']);
  }

  function removeScheduleSlot(idx) {
    setField('schedule', (editing?.schedule || []).filter((_, i) => i !== idx));
  }

  function updateScheduleSlot(idx, val) {
    const s = [...(editing?.schedule || [])];
    s[idx] = val;
    setField('schedule', s);
  }

  if (loading) {
    return (
      <Card>
        <div className="card p-4">
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading scheduled publications...
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="card p-4">
      <Toast ref={toast} />
      <div className="grid mb-4">
        <div className="col-6 flex align-items-center">
          <h2 className="m-0 main-text font-bold vertical-align-middle">
            <span>Scheduled publications</span>
            <Badge
              className="ml-2 b-4 relative"
              value={pubs.length ?? 0}
              style={{ bottom: '3px', backgroundColor: '#2020A0' }}
            />
          </h2>
        </div>
        <div className="col-6 flex justify-content-end">
          <Button
            label="Add publication"
            icon="pi pi-plus"
            className="p-button-primary p-button-sm secondary-button"
            style={{ minWidth: 160 }}
            onClick={openNew}
          />
        </div>
      </div>

      <TabView>
        <TabPanel header="Schedules">
          <DataTable
            value={pubs}
            stripedRows
            showGridlines
            className="p-datatable-sm"
            emptyMessage="No publications configured."
          >
            <Column header="Group" body={(row) => row.group_name || row.group_id || '—'} />
            <Column header="Channel" field="channel" />
            <Column header="Entry page" field="entry_page_slug" />
            <Column header="Ports" body={(row) => (row.ports || []).join(', ')} />
            <Column header="Schedule" body={(row) => (row.schedule || []).join(', ')} />
            <Column header="Timezone" field="timezone" />
            <Column
              header="Active"
              body={(row) => (
                <InputSwitch checked={row.active} onChange={() => toggleActive(row)} />
              )}
            />
            <Column
              header="Configure"
              body={(row) => (
                <div className="flex justify-content-center">
                  <Button
                    icon="pi pi-cog"
                    className="p-button-text p-button-sm"
                    onClick={() => openEdit(row)}
                    tooltip="Edit publication"
                    tooltipOptions={{ position: 'top' }}
                  />
                  <Button
                    icon="pi pi-trash"
                    className="p-button-text p-button-sm p-button-danger"
                    onClick={() => deletePub(row._id)}
                    tooltip="Delete publication"
                    tooltipOptions={{ position: 'top' }}
                  />
                </div>
              )}
              style={{ width: '8rem', textAlign: 'center' }}
            />
          </DataTable>
        </TabPanel>

        <TabPanel header="History">
          <DataTable
            value={history}
            stripedRows
            showGridlines
            className="p-datatable-sm"
            paginator
            rows={20}
            emptyMessage="No history yet."
          >
            <Column header="Date" field="scheduled_date" sortable />
            <Column header="Time" field="time_slot" />
            <Column
              header="Group"
              body={(row) => {
                const pub = pubs.find((p) => String(p._id) === String(row.publication_id));
                return pub?.group_name || pub?.group_id || row.publication_id;
              }}
            />
            <Column
              header="Status"
              body={(row) => (
                <Tag
                  value={row.status}
                  severity={row.status === 'sent' ? 'success' : row.status === 'failed' ? 'danger' : 'warning'}
                />
              )}
            />
            <Column header="Error" field="error" style={{ maxWidth: '200px' }} />
          </DataTable>
        </TabPanel>
      </TabView>

      <Dialog
        header={editing?._id ? 'Editar publicación' : 'Nueva publicación'}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        style={{ width: '550px' }}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Cancelar" severity="secondary" onClick={() => setDialogVisible(false)} />
            <Button label="Guardar" onClick={savePub} />
          </div>
        }
      >
        {editing && (
          <div className="flex flex-column gap-3">
            {/* Channel */}
            <div>
              <label className="font-bold block mb-1">Canal</label>
              <Dropdown value={editing.channel} options={CHANNELS} onChange={(e) => setField('channel', e.value)} className="w-full" />
            </div>

            {/* Group ID */}
            <div>
              <label className="font-bold block mb-1">Group ID (@g.us)</label>
              <InputText value={editing.group_id} onChange={(e) => setField('group_id', e.target.value)} className="w-full" placeholder="120363...@g.us" />
            </div>

            {/* Group Name */}
            <div>
              <label className="font-bold block mb-1">Group Name</label>
              <InputText value={editing.group_name} onChange={(e) => setField('group_name', e.target.value)} className="w-full" placeholder="Group Name" />
            </div>

            {/* Match By */}
            <div>
              <label className="font-bold block mb-1">Match By</label>
              <div className="flex gap-3">
                {MATCH_BY_OPTIONS.map((opt) => (
                  <div key={opt.key} className="flex align-items-center gap-2">
                    <Checkbox
                      checked={(editing.match_by || []).includes(opt.key)}
                      onChange={() => toggleArrayField('match_by', opt.key)}
                    />
                    <span>{opt.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Send Mode */}
            <div>
              <label className="font-bold block mb-1">Send Mode (orden de intento)</label>
              <div className="flex gap-3">
                {SEND_MODES.map((m) => (
                  <div key={m} className="flex align-items-center gap-2">
                    <Checkbox
                      checked={(editing.send_mode || []).includes(m)}
                      onChange={() => toggleArrayField('send_mode', m)}
                    />
                    <span>{m}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Entry Page */}
            <div>
              <label className="font-bold block mb-1">Entry Page</label>
              <Dropdown
                value={editing.entry_page_slug}
                options={entryPages.map((ep) => ({ label: ep.title || ep.slug, value: ep.slug || ep.full_slug, full_slug: ep.full_slug }))}
                onChange={(e) => {
                  setField('entry_page_slug', e.value);
                  setField('ports', []);
                  setField('port_labels', []);
                }}
                itemTemplate={(option) => (
                  <div>
                    <div className="font-bold">{option.label}</div>
                    <div className="text-sm" style={{ color: '#888' }}>{option.full_slug}</div>
                  </div>
                )}
                filterInputAutoFocus
                clearable
                className="w-full"
                placeholder="Seleccionar"
                filter
              />
            </div>

            {/* Ports grouped by crossing name */}
            {portGroups.length > 0 && (
              <div>
                <label className="font-bold block mb-1">Puertos</label>
                <div className="flex flex-column gap-3">
                  {portGroups.map((group, gi) => (
                    <div key={gi}>
                      <div className="font-semibold mb-1" style={{ fontSize: '0.9rem' }}>{group.crossing_name}</div>
                      <div className="flex flex-column gap-1 pl-3">
                        {group.ports.map((p) => (
                          <div key={p.number} className="flex align-items-center gap-2">
                            <Checkbox
                              checked={(editing.ports || []).includes(p.number)}
                              onChange={() => togglePort(p.number)}
                            />
                            <span>{p.label || p.number} ({p.number})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Schedule */}
            <div>
              <label className="font-bold block mb-1">Horarios</label>
              {(editing.schedule || []).map((slot, idx) => (
                <div key={idx} className="flex align-items-center gap-2 mb-2">
                  <InputText
                    type="time"
                    step="1"
                    value={slot}
                    onChange={(e) => updateScheduleSlot(idx, e.target.value)}
                    style={{ width: '200px' }}
                  />
                  <Button icon="pi pi-times" severity="danger" text size="small" onClick={() => removeScheduleSlot(idx)} />
                </div>
              ))}
              <Button label="+ Horario" size="small" text onClick={addScheduleSlot} />
            </div>

            {/* Timezone */}
            <div>
              <label className="font-bold block mb-1">Timezone</label>
              <Dropdown
                value={editing.timezone}
                options={TIMEZONES.map((tz) => ({ label: tz, value: tz }))}
                onChange={(e) => setField('timezone', e.value)}
                className="w-full"
                filter
              />
            </div>

            {/* Site URL (derived from entry page) */}
            <div>
              <label className="font-bold block mb-1">URL de publicación</label>
              <InputText
                value={editing.entry_page_slug ? `https://bordergarita.com/es/mexico/${editing.entry_page_slug}` : ''}
                disabled
                className="w-full"
              />
            </div>

            {/* Active */}
            <div className="flex align-items-center gap-2">
              <InputSwitch checked={editing.active !== false} onChange={(e) => setField('active', e.value)} />
              <span>Activo</span>
            </div>
          </div>
        )}
      </Dialog>
      </div>
    </Card>
  );
}
